import { WrapResponse } from '@/types';
import { StateSlice } from '@/stores/appStore';
import { snakeToCamel } from '@/lib/utils';
import { fun } from '@/lib/request';
import toast from 'react-hot-toast';
import { api } from '@/lib/env';

// 发票记录类型（后端返回格式）
export interface InvoiceRecordResponse {
  invoiceId: number;
  userId: number;
  amount: number;
  category: string;
  companyName: string;
  taxNumber: string;
  email: string;
  status: number; // 0: 待开票, 2: 已开票
  remark: string;
  downloadUrl: string;
  createTime: string;
}

// 发票列表响应类型
export interface InvoiceListData {
  list: InvoiceRecordResponse[];
}

// 申请开票请求参数
export interface ApplyInvoiceParams {
  orderNo: string[]; // 订单号数组
  category: string; // 发票类目
  companyName: string; // 企业抬头
  taxNumber: string; // 企业税号
  email: string; // 电子邮箱
  remark?: string; // 备注信息（可选）
}

const namespace = 'invoice';
export type InvoiceSlice = {
  [namespace]: {
    fetchInvoiceListLoading: boolean;
    fetchInvoiceList: () => Promise<InvoiceRecordResponse[] | null>;
    applyInvoiceLoading: boolean;
    applyInvoice: (params: ApplyInvoiceParams) => Promise<boolean>;
  }
}

export const createInvoiceSlice: StateSlice<InvoiceSlice> = ((set, _get) => {
  const get = () => _get()[namespace];
  return {
    [namespace]: {
      fetchInvoiceListLoading: false,
      fetchInvoiceList: async () => {
        try {
          set(({ [namespace]: state }) => {
            state.fetchInvoiceListLoading = true;
          });
          const responseData = (await fun.post<WrapResponse<InvoiceListData>>(
            api('/invoice/list', true),
            {},
          )).data;
          if (responseData.code === 0) {
            const data = snakeToCamel<InvoiceListData>(responseData.data);
            return data.list || [];
          } else {
            toast.error(responseData.message);
            return null;
          }
        } catch (error) {
          console.error('Error fetch invoice list:', error);
          toast.error('获取发票列表失败');
          return null;
        } finally {
          set(({ [namespace]: state }) => {
            state.fetchInvoiceListLoading = false;
          });
        }
      },
      applyInvoiceLoading: false,
      applyInvoice: async (params) => {
        try {
          set(({ [namespace]: state }) => {
            state.applyInvoiceLoading = true;
          });
          const responseData = (await fun.post<WrapResponse<{}>>(
            api('/invoice/apply', true),
            {
              order_no: params.orderNo,
              category: params.category,
              company_name: params.companyName,
              tax_number: params.taxNumber,
              email: params.email,
              remark: params.remark || '',
            },
          )).data;
          if (responseData.code === 0) {
            toast.success('发票申请提交成功');
            return true;
          } else {
            toast.error(responseData.message);
            return false;
          }
        } catch (error) {
          console.error('Error apply invoice:', error);
          toast.error('提交发票申请失败');
          return false;
        } finally {
          set(({ [namespace]: state }) => {
            state.applyInvoiceLoading = false;
          });
        }
      },
    },
  };
});

